"use client";

import { useState, useEffect, useRef } from "react";
import { createClient } from "@/lib/supabase/client";
import { isDateBlocked, isDateRangeBlocked } from "@/lib/utils/rental-blocks";
import type { Product } from "@/types";
import type {
  ProductRentalBlock,
  BranchOperationBlock,
} from "@/types";

/**
 * Hook to check availability for a list of products given a date range.
 *
 * - Daily products: checks all dates in [checkin, checkout)
 * - Timeslot products: checks checkin date only
 *
 * Returns a Map<productId, isUnavailable>.
 * Only fetches when both checkinDate and at least some products are provided.
 */
export function useProductAvailability(
  products: Product[],
  checkinDate: string | null,
  checkoutDate: string | null
): Map<string, boolean> {
  const [unavailableMap, setUnavailableMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // 날짜가 없으면 초기화
    if (!checkinDate || products.length === 0) {
      setUnavailableMap(new Map());
      return;
    }

    // 이전 요청 취소
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const checkAvailability = async () => {
      const supabase = createClient();
      const productIds = products.map((p) => p.id);

      // Batch fetch product rental blocks.
      // IMPORTANT: branch_id must be selected so the isDateBlocked util can
      // distinguish product-wide blocks (branch_id NULL) from
      // product×branch combo blocks (branch_id set).
      const { data: productBlocks } = await supabase
        .from("product_rental_blocks")
        .select(
          // timeslot_orders is required so isDateBlocked can skip slot-scoped
          // blocks (a partial slot block must NOT hide a timeslot product).
          // method_type is required so 택배/숙소배송 전용 제한(method-scoped rows)
          // are NOT mistaken for whole-product blocks here — this list view only
          // ever passes real branch ids (or ""), so such rows correctly never match
          // and the product stays listed as available for its other methods.
          "id, product_ids, branch_id, method_type, start_date, end_date, repeat_type, weekly_days, monthly_day, timeslot_orders, reason",
        )
        .overlaps("product_ids", productIds);

      if (controller.signal.aborted) return;

      // Collect all branch IDs from products
      const branchIds = products.flatMap(
        (p) => p.branches?.map((b) => b.id) ?? []
      );

      // Batch fetch branch operation blocks (only if there are branches)
      let branchBlocks: BranchOperationBlock[] = [];
      if (branchIds.length > 0) {
        const { data } = await supabase
          .from("branch_operation_blocks")
          .select("id, branch_id, start_date, end_date, repeat_type, weekly_days, monthly_day, reason")
          .in("branch_id", branchIds);

        if (controller.signal.aborted) return;
        branchBlocks = (data ?? []) as BranchOperationBlock[];
      }

      const allProductBlocks = (productBlocks ?? []) as ProductRentalBlock[];

      // Check each product
      const result = new Map<string, boolean>();
      const checkin = new Date(checkinDate + "T00:00:00");
      const checkout = checkoutDate
        ? new Date(checkoutDate + "T00:00:00")
        : null;

      for (const product of products) {
        // Per-product rows only. A 파트너 전체 택배 제한 row (product_ids = [])
        // cannot reach here — `.overlaps` never matches an empty array and this
        // filter drops it — and that is correct: a method-scoped restriction
        // leaves the product's other fulfilment methods open, so it must not
        // mark the product unavailable in listings. (productBlockAppliesTo also
        // fails closed on an empty product_ids row with no method_type.)
        const pBlocks = allProductBlocks.filter(
          (b) => b.product_ids.includes(product.id)
        );
        const isTimeslot = product.rental_unit === "timeslot";
        const productBranches = product.branches ?? [];

        // 지점이 없으면 상품 블록만 체크 (branchId="" → branch 블록 매칭 안 됨)
        if (productBranches.length === 0) {
          const blocked = isTimeslot || !checkout
            ? isDateBlocked(checkin, product.id, "", pBlocks, branchBlocks)
            : isDateRangeBlocked(checkin, checkout, product.id, "", pBlocks, branchBlocks);
          result.set(product.id, blocked);
          continue;
        }

        // 모든 지점이 막혀야 예약마감, 하나라도 가능하면 예약 가능
        let allBranchesBlocked = true;
        for (const branch of productBranches) {
          const blocked = isTimeslot || !checkout
            ? isDateBlocked(checkin, product.id, branch.id, pBlocks, branchBlocks)
            : isDateRangeBlocked(checkin, checkout, product.id, branch.id, pBlocks, branchBlocks);

          if (!blocked) {
            allBranchesBlocked = false;
            break; // 하나라도 가능하면 즉시 종료
          }
        }

        result.set(product.id, allBranchesBlocked);
      }

      if (!controller.signal.aborted) {
        setUnavailableMap(result);
      }
    };

    checkAvailability();

    return () => {
      controller.abort();
    };
  }, [products, checkinDate, checkoutDate]);

  return unavailableMap;
}
